import * as d3 from 'd3';
import * as $ from 'jquery';

import getElementSize from '../../../common/helpers/getElementSize';
import { axisFormat } from '../../../components/taskProgress/axisFormat';
import { DateFormatHelper } from '../../../wtFramework';
import { LastProcessedDisplay } from '../../../models';
import { leftMargin } from './leftMargin';

interface IPlotMargins {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export class LastProcessedTimeline {
    private _plotMargins: IPlotMargins = {
        top: 20,
        bottom: 0,
        left: leftMargin,
        right: 0
    };
    private _data: LastProcessedDisplay[];
    private _formatHelper: any;
    callback: (d: LastProcessedDisplay) => void;

    constructor(private _element: Element, plotMargins: IPlotMargins) {
        this._formatHelper = new DateFormatHelper();
        if (plotMargins) {
            this._plotMargins = plotMargins;
        }
        this.init();
        ($(_element) as any).resize(() => {
            this.render(false, false);
        });
    }

    private init() {
        const svg = d3.select(this._element)
            .select('svg');
        const plotGroup = svg.append('g')
            .classed('plot', true)
            .attr('transform', `translate(${this._plotMargins.left},${this._plotMargins.top})`);
        plotGroup.append('g')
            .classed('series', true);
        plotGroup.append('g')
            .classed('axis', true)
            .classed('x-axis', true);
        this.render(false, false);
    }

    data(value: any[]): this {
        if (arguments.length) {
            this._data = value;
        }
        return this;
    }

    render(isClickable: boolean, displayOnlyResults: boolean) {
        try {
            const defaultFormat = d3.time.format('%Y-%m-%d %H:%M');
            const svg = d3.select(this._element)
                .select('svg')
                .style('display', 'none');
            const size = getElementSize(this._element);
            const plotWidth = size.width - this._plotMargins.left - this._plotMargins.right;
            const plotHeight = size.height - this._plotMargins.top - this._plotMargins.bottom;
            svg.style('display', null)
                .attr('width', size.width)
                .attr('height', size.height);
            if (this._data) {
                const min = d3.min(this._data, d => d.beginDate);
                const max = d3.max(this._data, d => d.endDate);
                const { format, ticks } = axisFormat(min, max, plotWidth, this._formatHelper);
                const timeFormat = d3.time.format(format.format);
                const xScale = d3.time.scale()
                    .range([0, plotWidth])
                    .domain([min, max]);
                const xAxis = d3.svg.axis()
                    .orient('top')
                    .scale(xScale)
                    .tickFormat(timeFormat)
                    .tickValues(ticks);
                svg.select('.x-axis')
                    .call(xAxis);
                const dataBound = svg.select('g.series')
                    .selectAll('g.result-zone')
                    .data(this._data);
                dataBound
                    .exit()
                    .remove();
                const enterSelection = dataBound
                    .enter()
                    .append('g')
                    .classed('result-zone', true);
                enterSelection.append('rect');
                enterSelection.append('title');
                const spacing = .3;
                dataBound
                    .select('rect')
                    .attr('width', d => xScale(d.endDate) - xScale(d.beginDate))
                    .attr('x', d => xScale(d.beginDate))
                    .attr('y', plotHeight * (spacing) / 2)
                    .attr('height', plotHeight * (1 - spacing))
                    .style('fill', d => d.fill)
                    .attr('class', d => d.classed);
                if (isClickable) {
                    dataBound
                        .select('rect')
                        .on('click', (d) => this.callback(d))
                        .attr('class', d => d.classed + ' clickable');
                }
                if (displayOnlyResults) {
                    const xAxis = svg.select('.x-axis');
                    if (xAxis) {
                        xAxis.style('display', 'none');
                    }
                }
                dataBound.select('title')
                    .text(d => `${defaultFormat(d.beginDate)} - ${defaultFormat(d.endDate)}`);
            }
        } catch (e) {
            console.error(e);
        }
    }
}